import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { Location } from 'history';
import { prams } from 'mk/autogenerated/bzProps/prams';
import {
    STROLLER_FEATURE_ON_MARKET_FROM,
    STROLLER_FEATURE_PRICE_RANGE_from_to,
    STROLLER_FEATURE_WEIGHT_IN_KG, STROLLER_UNFOLDED_DIMENSIONS_IN_CM_WIDTH,
} from 'mk/autogenerated/translations/FacetItem.b3a5463e16c3b2330cf13b2a581779ff'
import { slugify } from 'mk/common/utils';
import { PageMode } from 'mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants';
import styles from 'mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.mscss';
import {
    checkboxFields,
    facetLabels,
    facetOptions,
    Facet,
    FacetOption,
} from 'mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm';
import { FEATURES_LIST } from 'mk2/apps/strollers/features';
import { TypeFeature } from 'mk2/apps/strollers/features/options';
import { Link, OwnProps as LinkProps } from 'mk2/components/Link';
import { cacheLast } from 'mk2/helpers/cache';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';

function removeFromQS(search: string, name: string, value: string): string {
    return search.replace(new RegExp(`${name}=${encodeURIComponent(value)}&?`, 'g'), '').replace(/&$/, '');
}

export function renderFacetItem(
    name: Facet,
    value: string,
    options: FacetOption[],
    location: Location,
): [string, React.ReactNode] {
    const opt = options.find((o) => o.value === value);
    const labelWithName = opt.seoLabel ?? `${facetLabels[name]}: ${opt.label}`;
    const labelWithoutName = opt.seoLabel ?? opt.label;

    const facetItem = (
        <FacetItem
            key={`${name}_${slugify(opt.value)}`}
            to={{
                ...location,
                search: removeFromQS(location.search, name, opt.value),
            }}
        >
            {labelWithName}
        </FacetItem>
    );

    return [labelWithoutName, facetItem];
}

interface FacetItemProps { to?: LinkProps['to']; }

const FacetItem: React.FC<FacetItemProps> = ({ to, children }) => (
    <div className={cx(styles.FacetItem, !to && styles['FacetItem--nonRemovable'])}>
        {children}
        {to && (
            <Link className={styles.FacetItem__times} to={to}>
                <FontAwesomeIcon icon={faTimes} />
            </Link>
        )}
    </div>
);

FacetItem.displayName = 'FacetItem';

export default React.memo<FacetItemProps>(FacetItem);


const extractFacetItemsCache = cacheLast(true);

export function extractFacetItems(pageMode, formData, location) {
    const key = `${pageMode}-${location.pathname}${location.search}`;
    return extractFacetItemsCache(() => {
        const stringFacetItems: string[] = [];
        const renderedFacetItems: React.ReactNode[] = [];
        let isLandingPageForStrollerType = null;
        let isAnyOtherFacetThanType = false;

        if (
            pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY ||
            pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE
        ) {
            if (formData.strollers_query) {
                stringFacetItems.push(formData.strollers_query);
                renderedFacetItems.push(
                    <FacetItem
                        key="strollers_query"
                        to={{
                            ...location,
                            search: removeFromQS(location.search, 'strollers_query', formData.strollers_query),
                        }}
                    >
                        {formData.strollers_query}
                    </FacetItem>,
                );
            }
            for (const name of Object.keys(facetOptions) as Facet[]) {
                // Ignore empty facets
                if (
                    // Array of values
                    (Array.isArray(formData[name]) && formData[name].length === 0) ||
                    // Single value
                    (!Array.isArray(formData[name]) && !formData[name]) ||
                    // Ignore availability
                    name === 'availability'
                ) {
                    continue;
                }

                if (name !== 'type') {
                    isAnyOtherFacetThanType = true;
                }

                const options = facetOptions[name];
                const values = Array.isArray(formData[name]) ? formData[name] : [formData[name]];
                for (const val of values) {
                    const [stringFacetItem, renderedFacetItem] = renderFacetItem(name, val, options, location);
                    stringFacetItems.push(stringFacetItem);
                    renderedFacetItems.push(renderedFacetItem);

                    if (name === 'type' && formData[name].length === 1) {
                        if (!Array.isArray(val) && Boolean(val)) {
                            isLandingPageForStrollerType = (FEATURES_LIST.type as TypeFeature).LONG_NAME[val];
                        }
                        if (Array.isArray(val) && val.length === 1) {
                            isLandingPageForStrollerType = (FEATURES_LIST.type as TypeFeature).LONG_NAME[val[0]];
                        }
                    }
                }
            }

            for (const name of Object.keys(checkboxFields)) {
                // Ignore empty facets
                if (!formData[name]) {
                    continue;
                }

                stringFacetItems.push(checkboxFields[name]);
                renderedFacetItems.push(
                    <FacetItem
                        key={`checkbox_field_${name}`}
                        to={{
                            ...location,
                            search: removeFromQS(location.search, name, 'true'),
                        }}
                    >
                        {checkboxFields[name]}
                    </FacetItem>,
                );
            }

            if (formData.priceFrom || formData.priceTo) {
                stringFacetItems.push(
                    `${facetLabels.price}: ${interpolate(STROLLER_FEATURE_PRICE_RANGE_from_to, {
                        from: formData.priceFrom || 0,
                        to: formData.priceTo || 999999,
                    })}`,
                );
                renderedFacetItems.push(
                    <FacetItem
                        key="custom-price"
                        to={{
                            ...location,
                            search: removeFromQS(
                                removeFromQS(location.search, 'priceFrom', formData.priceFrom),
                                'priceTo',
                                formData.priceTo,
                            ),
                        }}
                    >
                        {facetLabels.price}:{' '}
                        {interpolate(STROLLER_FEATURE_PRICE_RANGE_from_to, {
                            from: formData.priceFrom || 0,
                            to: formData.priceTo || 999999,
                        })}
                    </FacetItem>,
                );
            }

            if (formData.color) {
                const colorsInfo = prams.p_color.map((p, index) => ({
                    label: p[1],
                    filterValue: p[1],
                    value: p[0],
                    count: (index + 1) * 5,
                }));
                const color = [];
                for (let j = 0; j < formData.color.length; j++){
                    for (let i = 0; i < colorsInfo.length; i++){
                        if (colorsInfo[i].value === formData.color[j]){
                            color.push(colorsInfo[i]);
                            break;
                        }
                    }
                }
                for (let i = 0; i < color.length; i++) {
                    stringFacetItems.push(color[i].label);
                    renderedFacetItems.push(
                        <FacetItem
                            key={i}
                            to={{
                                ...location,
                                search: removeFromQS(location.search, 'color', formData.color),
                            }}
                        >
                            <div
                                className={
                                    cx(styles.FacetItem__color,
                                    styles[`FacetItem__color--p_color-${color[i].value}`],
                                    )}
                            />
                            {color[i].label}
                        </FacetItem>,
                    );
                }
            }

            if (formData.weight_in_kg_min || formData.weight_in_kg_max) {
                stringFacetItems.push(
                    `${STROLLER_FEATURE_WEIGHT_IN_KG}: ${formData.weight_in_kg_min || 0} - ${
                        formData.weight_in_kg_max || 999
                    }`,
                );
                renderedFacetItems.push(
                    <FacetItem
                        key="weight-in-kg"
                        to={{
                            ...location,
                            search: removeFromQS(
                                removeFromQS(location.search, 'weight_in_kg_min', formData.weight_in_kg_min),
                                'weight_in_kg_max',
                                formData.weight_in_kg_max,
                            ),
                        }}
                    >
                        {STROLLER_FEATURE_WEIGHT_IN_KG}: {formData.weight_in_kg_min || 0} -{' '}
                        {formData.weight_in_kg_max || 999}
                    </FacetItem>,
                );
            }

            if (formData.unfolded_dimensions_in_cm_width_min || formData.unfolded_dimensions_in_cm_width_max) {
                stringFacetItems.push(
                    `${STROLLER_UNFOLDED_DIMENSIONS_IN_CM_WIDTH}: ${
                        formData.unfolded_dimensions_in_cm_width_min || 0
                    } - ${formData.unfolded_dimensions_in_cm_width_max || 999}`,
                );
                renderedFacetItems.push(
                    <FacetItem
                        key="weight-in-kg"
                        to={{
                            ...location,
                            search: removeFromQS(
                                removeFromQS(
                                    location.search,
                                    'unfolded_dimensions_in_cm_width_min',
                                    formData.unfolded_dimensions_in_cm_width_min,
                                ),
                                'unfolded_dimensions_in_cm_width_max',
                                formData.unfolded_dimensions_in_cm_width_max,
                            ),
                        }}
                    >
                        {STROLLER_UNFOLDED_DIMENSIONS_IN_CM_WIDTH}: {formData.unfolded_dimensions_in_cm_width_min || 0}{' '}
                        - {formData.unfolded_dimensions_in_cm_width_max || 999}
                    </FacetItem>,
                );
            }

            if (formData.on_market_from_min || formData.on_market_from_max) {
                stringFacetItems.push(
                    `${STROLLER_FEATURE_ON_MARKET_FROM}: ${formData.on_market_from_min || 0} - ${
                        formData.on_market_from_max || new Date().getFullYear()
                    }`,
                );
                renderedFacetItems.push(
                    <FacetItem
                        key="weight-in-kg"
                        to={{
                            ...location,
                            search: removeFromQS(
                                removeFromQS(location.search, 'on_market_from_min', formData.on_market_from_min),
                                'on_market_from_max',
                                formData.on_market_from_max,
                            ),
                        }}
                    >
                        {STROLLER_FEATURE_ON_MARKET_FROM}: {formData.on_market_from_min || 0} -{' '}
                        {formData.on_market_from_max || new Date().getFullYear()}
                    </FacetItem>,
                );
            }

            // Custom render for availability - cannot be deleted, because always has to be selected
            if (formData.availability) {
                // const opt = facetOptions.availability.find((o) => o.value === formData.availability);
                if (formData.availability === 'available') {
                    // ignore from stringFacetItems
                    // ignore from renderedFacetItems
                } else {
                    const opt = facetOptions.availability.find((o) => o.value === formData.availability);
                    stringFacetItems.push(opt.seoLabel ?? opt.label);
                    renderedFacetItems.push(
                        <FacetItem
                            key="availability"
                            to={{
                                ...location,
                                search: removeFromQS(location.search, 'availability', formData.availability),
                            }}
                        >
                            {opt.seoLabel ?? opt.label}
                        </FacetItem>,
                    );
                }
            }

            if (isAnyOtherFacetThanType) {
                isLandingPageForStrollerType = null;
            }
        }
        return { isLandingPageForStrollerType, stringFacetItems, renderedFacetItems };
    }, key);
}
